import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save, Shield, ChevronLeft, Eye, EyeOff, UserPlus, Sparkles, Copy, RotateCcw, Palette, Upload, Trash2, Image as ImageIcon, Users } from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { getAdminSettings, updateAdminSettings, previewAdminPrompt, uploadAdminRefImage, deleteAdminRefImage, uploadAdminCategoryRefImage, deleteAdminCategoryRefImage } from '../lib/api';
import { toast } from 'sonner';
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const ADMIN_EMAIL = (process.env.REACT_APP_ADMIN_EMAIL || '').trim().toLowerCase();
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// reference_image is stored as "/api/uploads/admin/<file>" by legacy convention,
// but the backend's static mount is /uploads — strip the /api prefix for display.
const refImageDisplayUrl = (rel) => {
  if (!rel) return '';
  const path = rel.replace(/^\/api/, '');
  return `${API_URL}${path}`;
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Active image generation provider — "nanobanana" (default) or "replicate".
  const [imageProvider, setImageProvider] = useState('nanobanana');

  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [registrationPassword, setRegistrationPassword] = useState('TylerDuren');
  const [showRegPassword, setShowRegPassword] = useState(false);

  // Pre-prompts: defaults come from the server (DEFAULT_PREPROMPTS),
  // overrides is the editable map saved to admin_settings.categorized_prompts.
  const [defaultPrompts, setDefaultPrompts] = useState({});
  const [overrides, setOverrides] = useState({});
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Global style reference image (admin-uploaded, applied to every generation).
  const [referenceImage, setReferenceImage] = useState('');
  const [uploadingRef, setUploadingRef] = useState(false);
  const refImageInputRef = useRef(null);
  // Per-category reference images
  const [categoryRefs, setCategoryRefs] = useState({ characters: '', decors: '', objects: '' });
  const [uploadingCatRef, setUploadingCatRef] = useState('');
  const catRefInputRefs = { characters: useRef(null), decors: useRef(null), objects: useRef(null) };

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/', { replace: true });
        return;
      }
      const userEmail = (user.email || '').trim().toLowerCase();
      // If REACT_APP_ADMIN_EMAIL is configured, do an eager client-side check.
      // If it's missing, skip the eager check and let the backend's 403 decide.
      if (ADMIN_EMAIL && userEmail !== ADMIN_EMAIL) {
        navigate('/', { replace: true });
        return;
      }
      loadSettings();
    }
  }, [user, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = async () => {
    try {
      const data = await getAdminSettings();
      setRegistrationEnabled(!!data.registration_password_enabled);
      setRegistrationPassword(data.registration_password || 'TylerDuren');
      setDefaultPrompts(data.default_prompts || {});
      setOverrides({ ...(data.categorized_prompts || {}) });
      setReferenceImage(data.reference_image || '');
      setCategoryRefs({
        characters: data.ref_image_characters || '',
        decors: data.ref_image_decors || '',
        objects: data.ref_image_objects || '',
      });
      setImageProvider(data.image_provider === 'replicate' ? 'replicate' : 'nanobanana');
    } catch (e) {
      if (e?.response?.status === 403) {
        navigate('/', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingRef(true);
    try {
      const res = await uploadAdminRefImage(file);
      setReferenceImage(res.path || '');
      toast.success("Image de référence enregistrée");
    } catch {
      toast.error("Échec de l'upload de l'image");
    } finally {
      setUploadingRef(false);
      if (refImageInputRef.current) refImageInputRef.current.value = '';
    }
  };

  const handleRefImageDelete = async () => {
    if (!window.confirm("Supprimer l'image de référence de style ?")) return;
    try {
      await deleteAdminRefImage();
      setReferenceImage('');
      toast.success("Image de référence supprimée");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip empty overrides so a blank textarea cleanly falls back to the default.
      const cleanedOverrides = Object.fromEntries(
        Object.entries(overrides).filter(([, v]) => typeof v === 'string' && v.trim().length > 0)
      );
      await updateAdminSettings({
        categorized_prompts: cleanedOverrides,
        registration_password_enabled: registrationEnabled,
        registration_password: registrationPassword,
        image_provider: imageProvider,
      });
      toast.success("Paramètres enregistrés !");
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="animate-spin w-5 h-5 text-muted-foreground" />
      </div>
    );
  }

  return (
    <Layout title="Administration" actions={
      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => navigate('/')}>
        <ChevronLeft className="w-3 h-3" /> Retour
      </Button>
    }>
      <div className="h-full overflow-y-auto">
        <div className="max-w-2xl mx-auto p-8 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Administration</h1>
            <p className="text-sm text-muted-foreground">Configuration de la génération IA et de l'inscription</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-2 text-sm ml-auto"
            onClick={() => navigate('/admin/users')}
          >
            <Users className="w-3.5 h-3.5" /> Supervision utilisateurs
          </Button>
        </div>

        {/* Image provider Section */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">Fournisseur d'images IA</h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Choisissez quelle API génère les images des storyboards. OpenAI utilise GPT‑image‑1.5 ;
            NanoBanana utilise les endpoints Fast / High (1K) de nanobananaapi.ai.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'nanobanana', label: 'NanoBanana', desc: 'nanobananaapi.ai (Fast / High 1K)' },
              { key: 'replicate', label: 'Replicate', desc: 'google/nano-banana sur Replicate' },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setImageProvider(p.key)}
                className={`flex flex-col items-start gap-0.5 p-3 rounded-lg border text-left transition-all duration-200 cursor-pointer ${
                  imageProvider === p.key
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border bg-background text-muted-foreground hover:border-primary/30'
                }`}
                data-testid={`image-provider-${p.key}`}
              >
                <span className="text-sm font-medium">{p.label}</span>
                <span className="text-[11px] text-muted-foreground">{p.desc}</span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground/80">
            {imageProvider === 'nanobanana' ? (
              <>Necessite la variable d'environnement <code className="px-1 py-0.5 rounded bg-muted text-foreground/80">NANOBANANA_API_KEY</code> cote serveur.</>
            ) : (
              <>Necessite la variable d'environnement <code className="px-1 py-0.5 rounded bg-muted text-foreground/80">REPLICATE_API_KEY</code> cote serveur.</>
            )}
          </p>
        </div>

        {/* Pre-prompts categorized configuration */}
        <PrePromptsSection
          defaults={defaultPrompts}
          overrides={overrides}
          setOverrides={setOverrides}
          previewPrompt={previewPrompt}
          setPreviewPrompt={setPreviewPrompt}
          previewLoading={previewLoading}
          setPreviewLoading={setPreviewLoading}
        />

        {/* Global style reference image */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Palette className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">
              Image de référence de style global
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Cette image définit le style graphique appliqué à toutes les générations
            (trait, niveaux de gris, cadrage). Elle n'influence pas le contenu des scènes.
          </p>

          {referenceImage ? (
            <div className="space-y-3">
              <div className="relative w-full max-w-sm rounded-lg border border-border overflow-hidden bg-secondary/20">
                <img
                  src={refImageDisplayUrl(referenceImage)}
                  alt="Référence de style"
                  className="w-full h-auto object-contain"
                  data-testid="ref-image-preview"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  onClick={() => refImageInputRef.current?.click()}
                  disabled={uploadingRef}
                  data-testid="ref-image-replace-btn"
                >
                  {uploadingRef ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                  Remplacer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5 text-destructive hover:text-destructive"
                  onClick={handleRefImageDelete}
                  data-testid="ref-image-delete-btn"
                >
                  <Trash2 className="w-3 h-3" /> Supprimer
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => refImageInputRef.current?.click()}
              disabled={uploadingRef}
              data-testid="ref-image-upload-btn"
            >
              {uploadingRef ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
              Importer une image
            </Button>
          )}

          <input
            ref={refImageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleRefImageUpload}
          />
        </div>

        {/* Per-category reference images for library generation */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <ImageIcon className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">
              Images de référence par catégorie
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">
            Utilisées automatiquement lors des générations bibliothèque si l'utilisateur n'importe pas d'images de référence.
          </p>
          {[
            { key: 'characters', label: 'Personnages' },
            { key: 'decors', label: 'Décors' },
            { key: 'objects', label: 'Objets' },
          ].map(({ key, label }) => (
            <div key={key} className="space-y-2">
              <Label className="text-xs font-medium">{label}</Label>
              {categoryRefs[key] ? (
                <div className="flex items-center gap-3">
                  <img
                    src={refImageDisplayUrl(categoryRefs[key])}
                    alt={label}
                    className="w-16 h-16 rounded-md border border-border object-cover bg-secondary/20"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                      onClick={() => catRefInputRefs[key].current?.click()}
                      disabled={uploadingCatRef === key}>
                      {uploadingCatRef === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                      Remplacer
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
                      onClick={async () => {
                        try {
                          await deleteAdminCategoryRefImage(key);
                          setCategoryRefs(p => ({ ...p, [key]: '' }));
                          toast.success(`Référence ${label} supprimée`);
                        } catch { toast.error('Échec'); }
                      }}>
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                  onClick={() => catRefInputRefs[key].current?.click()}
                  disabled={uploadingCatRef === key}>
                  {uploadingCatRef === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  Importer
                </Button>
              )}
              <input ref={catRefInputRefs[key]} type="file" accept="image/*" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (catRefInputRefs[key].current) catRefInputRefs[key].current.value = '';
                  if (!file) return;
                  setUploadingCatRef(key);
                  try {
                    const res = await uploadAdminCategoryRefImage(key, file);
                    setCategoryRefs(p => ({ ...p, [key]: res.path || '' }));
                    toast.success(`Référence ${label} mise à jour`);
                  } catch { toast.error('Échec de l\'upload'); }
                  finally { setUploadingCatRef(''); }
                }} />
            </div>
          ))}
        </div>

        {/* Site Gate Password Section */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="w-4 h-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">Code d'accès au site</h2>
          </div>

          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-secondary/30">
            <div>
              <Label className="text-sm font-medium">Activer le code d'accès</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Si activé, ce code est demandé avant tout accès au site (login, inscription, Google OAuth inclus).
              </p>
            </div>
            <Switch
              checked={registrationEnabled}
              onCheckedChange={setRegistrationEnabled}
              data-testid="toggle-site-gate"
            />
          </div>

          {registrationEnabled && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                Code d'accès
              </Label>
              <div className="relative">
                <Input
                  type={showRegPassword ? "text" : "password"}
                  value={registrationPassword}
                  onChange={(e) => setRegistrationPassword(e.target.value)}
                  placeholder="TylerDuren"
                  className="pr-10 text-sm font-mono"
                  data-testid="site-gate-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowRegPassword(!showRegPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showRegPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Communiquez ce code uniquement aux personnes que vous voulez autoriser à accéder au site. Les utilisateurs déjà passés gardent un accès pendant 30 jours.
              </p>
            </div>
          )}
        </div>

        {/* Save */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full gap-2"
          size="lg"
          data-testid="save-admin-settings-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer les paramètres
        </Button>
        </div>
      </div>
    </Layout>
  );
}


// ---------------------------------------------------------------------------
// Pre-prompts admin section. Reads default prompts + override map from props,
// renders one editable accordion per category, and offers a preview of the
// assembled prompt computed by the backend.
// ---------------------------------------------------------------------------

function PrePromptsSection({ defaults, overrides, setOverrides, previewPrompt, setPreviewPrompt, previewLoading, setPreviewLoading }) {
  const categories = Object.keys(defaults || {});

  const setOverride = useCallback((key, value) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  }, [setOverrides]);

  const resetCategory = useCallback((key) => {
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [setOverrides]);

  const handlePreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(overrides || {}).filter(([, v]) => typeof v === 'string' && v.trim().length > 0)
      );
      const res = await previewAdminPrompt({ categorized_prompts: cleaned });
      setPreviewPrompt(res.prompt || '');
    } catch (e) {
      toast.error("Impossible de prévisualiser le prompt");
    } finally {
      setPreviewLoading(false);
    }
  }, [overrides, setPreviewLoading, setPreviewPrompt]);

  const handleCopy = useCallback(async () => {
    if (!previewPrompt) return;
    try {
      await navigator.clipboard.writeText(previewPrompt);
      toast.success("Prompt copié dans le presse-papier");
    } catch {
      toast.error("Échec de la copie");
    }
  }, [previewPrompt]);

  if (!categories.length) {
    return null;
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">Pré-prompts (configuration avancée)</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Chaque catégorie contient un fragment de prompt utilisé lors de la génération d'images. Modifier
        une catégorie écrase la valeur par défaut. Laisser vide pour rétablir la valeur par défaut.
        Le cadrage reste choisi par l'utilisateur dans l'interface de génération — il n'est pas configurable ici.
      </p>

      <Accordion type="multiple" className="w-full">
        {categories.map((key) => {
          const defaultEntry = defaults[key] || { label: key, prompt: '' };
          const currentValue = overrides[key] ?? '';
          const isOverridden = typeof overrides[key] === 'string' && overrides[key].trim().length > 0;
          return (
            <AccordionItem key={key} value={key} className="border-border">
              <AccordionTrigger className="text-sm">
                <span className="flex items-center gap-2">
                  {defaultEntry.label}
                  {isOverridden && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">modifié</span>}
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                <Textarea
                  value={currentValue}
                  onChange={(e) => setOverride(key, e.target.value)}
                  placeholder={defaultEntry.prompt}
                  className="min-h-[120px] text-sm"
                  data-testid={`prompt-override-${key}`}
                />
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] text-muted-foreground">
                    Vide = valeur par défaut.
                  </p>
                  {isOverridden && (
                    <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1.5" onClick={() => resetCategory(key)} data-testid={`prompt-reset-${key}`}>
                      <RotateCcw className="w-3 h-3" /> Restaurer le défaut
                    </Button>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
        <AccordionItem value="__preview" className="border-border">
          <AccordionTrigger className="text-sm">
            <span className="flex items-center gap-2">
              Prompt final assemblé
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">aperçu</span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="space-y-2">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handlePreview} disabled={previewLoading} data-testid="prompt-preview-btn">
                {previewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Calculer l'aperçu
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleCopy} disabled={!previewPrompt} data-testid="prompt-copy-btn">
                <Copy className="w-3 h-3" /> Copier
              </Button>
            </div>
            <Textarea
              value={previewPrompt}
              readOnly
              placeholder="Cliquer sur « Calculer l'aperçu » pour assembler le prompt avec un échantillon de scène (le résultat reflète vos overrides actuels mais n'est pas encore enregistré)."
              className="min-h-[200px] text-xs font-mono whitespace-pre-wrap"
              data-testid="prompt-preview-output"
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
